/** external */
import _ from "lodash";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { SquareFilled } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";

/** internal */
import {
  convertStreamedXmlToQuestionChunk,
  getLastQuestionIdFromQuestions,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  createWorksheetQuestionAI,
  updateGeneratingAIQuestions,
  updateQuestionToCache,
  updateWorksheetSidebar,
  createWorksheetQuestionAIV2,
  updateActiveQuestion,
  updateAddQuestionsInBetween,
  updateAIAssessmentTools,
  updateQuestionOrder,
} from "Worksheet/modules/WorksheetModules";
import {
  AI_GEN_IDENTIFIER,
  GENERATE_USING_AI,
  QUESTION_END_TAG,
  QUESTION_START_TAG,
  QUESTION_TAG_REGEX,
  WORKSHEET_ELEMENT_IDS,
  WS_INSTRUCTION_START_TAG,
  WS_INSTRUCTION_END_TAG,
} from "Worksheet/modules/WorksheetConstants";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";
import DotLoadingIcon from "UIComponents/DotLoadingIcon";
import Loading from "UIComponents/Loading";
import { cancelRequest } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { updateAIConversation, uploadFileV2 } from "modules/Services";
import {
  updateAssessment,
  updateAssessmentInCache,
} from "Assessments/modules/AssessmentModule";

/** relative */
import {
  getQuestionTemplate,
  getQuestionTemplateV2,
} from "./QuestionTemplates";
import {
  generateFieldSet,
  processKey,
  parseData,
  getNonCachedLastQuestionId,
  AI_SUPPORTED_QUESTION_LABELS,
  getCurrentAIState,
  handleMetricAndMetaInitials,
  TYPE_OF_QUESTION_TAG,
  getWorksheetQuestionGeneratePromptOneResponse,
  sanitizeWorksheetInstruction,
  generateQuestionSequenceMap,
  generateFieldSetV2,
  getNonCachedLastQuestionIdV2,
  generateQuestionSequenceMapV2,
} from "./QuestionLoaderUtils";
import { updateQuestionToCacheV2 } from "./modules/QuestionLoaderModules";
import classes from "./QuestionLoader.scss";

/**
 *
 * @params { currentAIState }
 * @returns This component is responsible for displaying a loading indicator and message
 */
const WorkingOutOnQuestionsLoader = ({
  t,
  currentAIState,
  firstPromptResponse,
}) => {
  const { isWritting, isStreamingOrEnd } = getCurrentAIState(currentAIState);

  const loadingTitle = useMemo(() => {
    let title = t("worksheet:working_out_questions");

    if (!_.isEmpty(firstPromptResponse)) {
      title = t("worksheet:creating_a_plan");
      if (_.get(firstPromptResponse, "isPlanReceived", null)) {
        title = t("worksheet:working_out_questions");
      } else if (_.get(firstPromptResponse, "isQuestionReceived", null)) {
        title = t("worksheet:generating_questions");
      }
    }

    return title;
  }, [firstPromptResponse, t]);

  if (!(isWritting ? true : isStreamingOrEnd)) return null;

  return (
    <div className={classes.analysingUI}>
      <DotLoadingIcon />
      <div className="text-subtitle-2">{loadingTitle}</div>
    </div>
  );
};

/**
 *
 * @param {*} params
 * @returns Stop Generation AI Question Button
 */
const StopGeneratingQuestionButton = ({
  t,
  onClick,
  loading,
  activeQuestionId,
}) => {
  const stopGenerationStyles = classNames(classes.disableState, {
    [classes.analysingVisible]: _.isEqual(activeQuestionId, GENERATE_USING_AI),
    [classes.loader]: loading,
  });

  return (
    <div className={stopGenerationStyles}>
      {loading ? (
        <Loading />
      ) : (
        <Button
          dsVersion="2.0"
          size="large"
          variant="neutral"
          onClick={onClick}
          icon={<SquareFilled />}
          className={classes.stopGenerateBtn}
        >
          {t("common:stop_with_label", {
            label: t("classRoom:generating"),
          })}
        </Button>
      )}
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns AI Generated Question Loading Component
 */
const QuestionLoader = props => {
  const {
    messages,
    updateQuestionToCache,
    sections,
    worksheetMutationData,
    createWorksheetQuestionAI,
    createWorksheetQuestionAIV2,
    toggleAiQuestionGenerator,
    currentAIState,
    isGeneratingAIQuestions,
    updateGeneratingAIQuestions,
    activeQuestionId,
    t,
    getAssessmentQuestions,
    resetAIHistory,
    gradeId,
    aiConversationId,
    conversationMetadata,
    updateAIConversation,
    setCurrentAIState,
    updateWorksheetSidebar,
    updateAIAssessmentTools,
    isAddingQuestionsInBetween,
    parentResourceId,
    questionResourceId,
    step,
    goToSecondStep,
    updateActiveQuestion,
    descriptionField,
    updateAssessment,
    updateAssessmentInCache,
    uploadFileV2,
    updateAddQuestionsInBetween,
    isQueriesV2Enabled,
    parentItemId,
    questionId,
    updateQuestionToCacheV2,
  } = props;

  const [stepOneMetricId, setStepOneMetricId] = useState("");
  const [stepTwoMetricId, setStepTwoMetricId] = useState("");
  const [wsInstruction, setWsInstruction] = useState("");
  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  const [isTerminateTriggered, setIsTerminateTriggered] = useState(false);

  const onCancelRequest = _.isFunction(cancelRequest) ? cancelRequest : _.noop;

  const {
    content: responseContent,
    usageMetricsId,
  } = handleMetricAndMetaInitials({
    messages,
    aiConversationId,
    updateAIConversation,
    conversationMetadata,
    updateAIAssessmentTools,
    metadataController: {
      step,
      stepOneMetricId,
      stepTwoMetricId,
      setStepOneMetricId,
      setStepTwoMetricId,
    },
  });
  const content = useMemo(() => responseContent, [responseContent]);

  const [
    generatedQuestionCacheCount,
    setGeneratedQuestionCacheCount,
  ] = useState(0);
  const [generatedQuestionCount, setGeneratedQuestionCount] = useState(0);
  const [fieldIds, setFieldIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [stopTermination, setStopTermination] = useState(false);
  const [terminateTriggered, setTerminateTriggered] = useState(false);
  const [creationStatus, setCreationStatus] = useState({});
  const [sequenceToQuestionIdMap, setSequenceToQuestionIdMap] = useState({});

  // * Getting question Detail from Streamed XML
  const {
    text,
    questionType,
    dividedChunks,
    currentObject,
    currentObjectKey,
    parsedQuestionFields,
  } = useMemo(() => {
    const dividedChunks = convertStreamedXmlToQuestionChunk(content);
    const text = _.get(_.last(dividedChunks), "text", "");
    const updatedText = text.replace(QUESTION_TAG_REGEX, "");
    const parsedQuestionFields = parseData(updatedText);
    const currentObject = _.last(parsedQuestionFields);
    const currentObjectKey = _.first(_.keys(currentObject));

    const toqTag = _.get(
      parsedQuestionFields,
      ["0", TYPE_OF_QUESTION_TAG],
      "``"
    );
    const questionType = _.capitalize(toqTag?.replace(/`/g, ""));

    return {
      text,
      questionType,
      dividedChunks,
      currentObject,
      currentObjectKey,
      parsedQuestionFields,
    };
  }, [content]);

  const lastQuestionId = useMemo(() => {
    if (isQueriesV2Enabled) {
      return getLastQuestionIdFromQuestions(sections);
    } else {
      const questionEdges = _.get(_.last(sections), "childrenV2.edges", []);
      return _.get(_.last(questionEdges), "node.item.id", "");
    }
  }, [sections]);

  const firstPromptResponse = useMemo(() => {
    if (step == 1) {
      const promptTwoPayload = getWorksheetQuestionGeneratePromptOneResponse(
        content
      );
      return promptTwoPayload;
    }
    return {};
  }, [content, step]);

  useEffect(() => {
    if (
      step === 1 &&
      currentAIState === "END" &&
      !_.get(firstPromptResponse, "isQuestionReceived")
    ) {
      onCancelRequest();
      resetAIHistory(!isTerminateTriggered);
      setTerminateTriggered(false);
    }
  }, [currentAIState, step, firstPromptResponse]);

  useEffect(() => {
    if (step === 1 && currentAIState === "END") {
      if (
        !_.isEmpty(firstPromptResponse) &&
        _.get(firstPromptResponse, "isQuestionReceived") &&
        !_.isEmpty(_.get(firstPromptResponse, "value"))
      ) {
        onCancelRequest();
        goToSecondStep(
          _.get(firstPromptResponse, "value"),
          _.get(firstPromptResponse, "visualizationValue")
        );
      } else {
        onCancelRequest();
        terminateQuestionGeneration();
      }
    }
  }, [firstPromptResponse, currentAIState]);

  /**
   *
   * @param {*} type
   * @returns Function is responsible for creating question on server
   */
  const handleQuestionCreation = type => {
    // * When section ID is not available, terminate the question generation
    const shouldTerminate = isQueriesV2Enabled
      ? !parentItemId && !_.get(_.last(sections), "item.id", null)
      : !parentResourceId && !_.get(_.last(sections), "id", null);
    if (shouldTerminate) {
      terminateQuestionGeneration();
      return;
    }

    const fieldSet = isQueriesV2Enabled
      ? generateFieldSetV2()
      : generateFieldSet();
    const { questionId: fieldQuestionId } = fieldSet;
    const template = isQueriesV2Enabled
      ? getQuestionTemplateV2(fieldSet, type)
      : getQuestionTemplate(fieldSet, type);
    setFieldIds(fieldSet);

    const questionSwitchId =
      _.isEqual(activeQuestionId, GENERATE_USING_AI) ||
      _.includes(activeQuestionId, AI_GEN_IDENTIFIER)
        ? fieldQuestionId
        : null;

    // * Updating question in cache
    if (isQueriesV2Enabled) {
      updateQuestionToCacheV2({
        newQuestion: template,
        enableQuestionSwitch: _.isEqual(generatedQuestionCacheCount, 0),
        questionId: questionSwitchId,
        parentItemId: parentItemId || _.get(_.last(sections), "item.id", null),
        questionOffset: generatedQuestionCacheCount,
        isAddingQuestionsInBetween,
        putAfterQuestionId: questionId || lastQuestionId,
      });
    } else {
      updateQuestionToCache({
        newQuestion: template,
        questionId: questionSwitchId,
        enableQuestionSwitch: _.isEqual(generatedQuestionCacheCount, 0),
        parentResourceId,
        questionResourceId,
        questionOffset: generatedQuestionCacheCount,
        isAddingQuestionsInBetween,
      });
    }

    setGeneratedQuestionCacheCount(prev => prev + 1);
  };

  /**
   *
   * @description_update
   * Checking if worksheet instruction is in Ai response and currently empty
   * then updating the description field with the instruction in cache and calling updateAssessment
   */
  useEffect(() => {
    const wsDescription = _.get(descriptionField, "value", "");
    if (
      _.includes(content, WS_INSTRUCTION_START_TAG) &&
      _.isEmpty(wsInstruction) &&
      _.isEmpty(wsDescription)
    ) {
      if (_.includes(content, WS_INSTRUCTION_END_TAG)) {
        // * Extracting the instruction text from the response
        setWsInstruction("INSTRUCTION_ADDED");
        const processWorksheetInstruction = async () => {
          try {
            await sanitizeWorksheetInstruction(
              content,
              descriptionField,
              updateAssessment,
              updateAssessmentInCache,
              uploadFileV2
            );
          } catch (error) {}
        };
        processWorksheetInstruction();
      }
    }
  }, [content, descriptionField]);

  useEffect(() => {
    // * If the text contains question start tag, get streamed value, process it, create a field and save in cache
    if (_.includes(text, QUESTION_START_TAG)) {
      if (generatedQuestionCacheCount > 0 && !_.isEmpty(fieldIds)) {
        const value = _.get(currentObject, currentObjectKey, "``");
        processKey(
          currentObjectKey,
          value,
          fieldIds,
          questionType,
          parsedQuestionFields,
          isQueriesV2Enabled
        );
      }
      if (_.isEqual(_.size(dividedChunks), generatedQuestionCacheCount + 1)) {
        // * Creating AI Supported questions
        if (_.includes(AI_SUPPORTED_QUESTION_LABELS, questionType)) {
          handleQuestionCreation(questionType);
        }
      }
    }
  }, [
    text,
    fieldIds,
    questionType,
    currentObject,
    dividedChunks,
    currentObjectKey,
    parsedQuestionFields,
    generatedQuestionCacheCount,
  ]);

  /**
   * Adds a question to the worksheet using AI generation
   * @param {Object} params - The parameters for adding a question
   * @param {Array} params.questionData - The question data to be added
   * @param {number} params.aiSequence - The AI sequence number
   * @param {number} params.offset - The offset for the question
   * @returns {Promise<void>}
   */
  const addQuestionToWorksheet = async ({
    questionData,
    aiSequence,
    offset,
  }) => {
    const { parentId, parentType } = worksheetMutationData;
    const lastSection = _.last(sections);
    const { lastNodeId = null, lastQuestionId = null } = isQueriesV2Enabled
      ? getNonCachedLastQuestionIdV2(lastSection)
      : getNonCachedLastQuestionId(lastSection);

    // Set creation status to indicate this question is being processed
    setCreationStatus(prev => ({ ...prev, [aiSequence]: true }));

    try {
      let createdQuestion;
      let newCreatedQuestionId = null;

      if (isMutationsV2Enabled) {
        // Use V2 mutation (new version)
        createdQuestion = await createQuestionV2({
          parentId,
          parentType,
          lastSection,
          lastQuestionId,
          questionData,
          aiSequence,
          offset,
        });

        newCreatedQuestionId = _.get(
          createdQuestion,
          "data.content.createWorksheetQuestionsUsingAiV2.0.id",
          ""
        );
      } else {
        // Use V1 mutation (legacy version)
        createdQuestion = await createQuestionV1({
          parentId,
          parentType,
          lastSection,
          lastNodeId,
          questionData,
          aiSequence,
          offset,
        });

        newCreatedQuestionId = _.get(
          createdQuestion,
          "data.content.createWorksheetQuestionsUsingAI.0.id",
          ""
        );
      }

      // Update the sequence to question ID mapping
      setSequenceToQuestionIdMap(prev => ({
        ...prev,
        [aiSequence]: newCreatedQuestionId,
      }));
    } catch (error) {
      console.error("Error creating question:", error);
    } finally {
      // Mark this question as no longer being processed
      setCreationStatus(prev => ({ ...prev, [aiSequence]: false }));
    }
  };

  /**
   * Creates a question using the V2 mutation (new version)
   * @param {Object} params - The parameters for creating a question
   * @returns {Promise<Object>} The created question response
   */
  const createQuestionV2 = async ({
    parentId,
    lastSection,
    lastQuestionId,
    questionData,
    aiSequence,
    offset,
  }) => {
    const input = {
      worksheetId: parentId,
      parentId: parentItemId || _.get(lastSection, "item.id", null),
      parentType: "QUESTION",
      putAfterQuestionId: questionId || lastQuestionId,
      questionContent: questionData,
      aiSequence: [aiSequence],
      usageMetricsId,
      offset,
      gradeId,
    };

    return await createWorksheetQuestionAIV2({
      input,
      refetch: false,
      isQueriesV2Enabled,
    });
  };

  /**
   * Creates a question using the V1 mutation (legacy version)
   * @param {Object} params - The parameters for creating a question
   * @returns {Promise<Object>} The created question response
   */
  const createQuestionV1 = async ({
    parentId,
    parentType,
    lastSection,
    lastNodeId,
    questionData,
    aiSequence,
    offset,
  }) => {
    const input = {
      organizationResource: {
        parentId,
        parentType,
        parentResourceId: parentResourceId || _.get(lastSection, "id", null),
        organizationResources: [
          {
            type: "WORKSHEET_RESOURCE",
            resourceType: "QUESTION",
          },
        ],
        shouldHandleSequence: true,
        putAfterItemId: isAddingQuestionsInBetween
          ? questionResourceId
          : lastNodeId,
      },
      questionContent: questionData,
      aiSequence: [aiSequence],
      usageMetricsId,
      offset,
      gradeId,
    };

    return await createWorksheetQuestionAI({
      parentId,
      parentType,
      input,
      refetch: false,
    });
  };

  useEffect(() => {
    if (
      _.includes(
        _.get(dividedChunks[generatedQuestionCount], "text", ""),
        QUESTION_END_TAG
      ) &&
      _.size(dividedChunks) > generatedQuestionCount
    ) {
      setGeneratedQuestionCount(prev => prev + 1);
      addQuestionToWorksheet({
        questionData: [
          _.get(dividedChunks[generatedQuestionCount], "text", ""),
        ],
        aiSequence: generatedQuestionCount + 1,
        offset: generatedQuestionCount,
      });
    }
  }, [dividedChunks, generatedQuestionCount]);

  // * Terminating question generating and setting loading to false
  const terminateQuestionGeneration = async () => {
    setTerminateTriggered(true);
    setLoading(true);
    setStopTermination(true);
    updateWorksheetSidebar({ isOpen: true });
    setWsInstruction("");
    await onCancelRequest();
    await resetAIHistory();

    await new Promise(resolve => setTimeout(resolve, 500));
    await refetchQuestionData();

    const isAiGenActive = _.startsWith(activeQuestionId, AI_GEN_IDENTIFIER);
    if (isAiGenActive) updateActiveQuestion(null);

    if (
      step === 2 &&
      generatedQuestionCount === 0 &&
      _.isEmpty(creationStatus)
    ) {
      resetAIHistory(!isTerminateTriggered);
    }

    updateGeneratingAIQuestions(false);
    setIsTerminateTriggered(false);
  };

  // * Refetching Question data and resetting states
  const refetchQuestionData = useCallback(async () => {
    const { parentId } = worksheetMutationData;
    let questionSequenceMap;
    try {
      questionSequenceMap = isQueriesV2Enabled
        ? generateQuestionSequenceMapV2(sections, sequenceToQuestionIdMap)
        : generateQuestionSequenceMap(
            getAssessmentQuestions,
            sequenceToQuestionIdMap
          );
      if (!_.isEmpty(questionSequenceMap)) {
        await updateQuestionOrder(parentId, questionSequenceMap);
      }
    } catch (error) {}

    if (_.isFunction(getAssessmentQuestions?.refetch)) {
      await getAssessmentQuestions?.refetch();
    }
    setLoading(false);
    setGeneratedQuestionCount(0);
    setGeneratedQuestionCacheCount(0);
    setFieldIds({});
    updateGeneratingAIQuestions(false);
    setStopTermination(false);
    toggleAiQuestionGenerator(GENERATE_USING_AI, true);
    setCurrentAIState("");
    setTerminateTriggered(false);
    setCreationStatus({});
    setWsInstruction("");
    setSequenceToQuestionIdMap({});

    updateAddQuestionsInBetween({
      isAddingQuestionsInBetween: false,
      isAddingAtStartOfMultipart: false,
      questionResourceId: null,
      parentResourceId: null,
      nextQuestionResourceId: null,
      questionId: null,
      parentItemId: null,
      nextQuestionId: null,
    });
  }, [
    getAssessmentQuestions,
    updateGeneratingAIQuestions,
    toggleAiQuestionGenerator,
    setCurrentAIState,
    sequenceToQuestionIdMap,
  ]);

  // * Handle case when AI stops generating data midway
  useEffect(() => {
    // * Checking if all question created and added to server
    const creationStatusValues = _.values(creationStatus);
    const isAnyQuestionCreationPending = _.some(
      creationStatusValues,
      value => value
    );

    if (
      _.isEqual(currentAIState, "END") &&
      !terminateTriggered &&
      step == 2 &&
      stepTwoMetricId &&
      !isAnyQuestionCreationPending
    ) {
      terminateQuestionGeneration();
    }
  }, [
    currentAIState,
    terminateTriggered,
    step,
    stepOneMetricId,
    stepTwoMetricId,
    creationStatus,
    sequenceToQuestionIdMap,
  ]);

  return (
    <div className={classes.questionLoader}>
      <WorkingOutOnQuestionsLoader
        t={t}
        currentAIState={currentAIState}
        firstPromptResponse={firstPromptResponse}
      />

      {(isGeneratingAIQuestions || stopTermination) &&
        createPortal(
          <StopGeneratingQuestionButton
            t={t}
            loading={loading}
            activeQuestionId={activeQuestionId}
            onClick={async () => {
              setIsTerminateTriggered(true);
              setCurrentAIState("END");
              await onCancelRequest();
            }}
          />,
          document.getElementById(
            WORKSHEET_ELEMENT_IDS.WORKSHEET_QUESTION_CONTENT_AREA_ID
          )
        )}
    </div>
  );
};

const mapStateToProps = state => {
  const portalType = _.get(state, "login.userInfo.portalType");
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const activeQuestionId = _.get(state, "worksheet.activeQuestion");

  const isAddingQuestionsInBetween = _.get(
    state,
    "worksheet.addQuestionsInBetween.isAddingQuestionsInBetween",
    false
  );

  const questionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionResourceId",
    null
  );
  const parentResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentResourceId",
    null
  );

  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  const questionId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionId",
    null
  );

  const parentItemId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentItemId",
    null
  );
  return {
    portalType,
    isGeneratingAIQuestions,
    activeQuestionId,
    isAddingQuestionsInBetween,
    questionResourceId,
    parentResourceId,
    isQueriesV2Enabled,
    questionId,
    parentItemId,
  };
};

const mapActionCreators = {
  createWorksheetQuestionAI,
  createWorksheetQuestionAIV2,
  updateQuestionToCache,
  updateGeneratingAIQuestions,
  updateAIConversation,
  updateWorksheetSidebar,
  updateAIAssessmentTools,
  updateActiveQuestion,
  updateAssessment,
  updateAssessmentInCache,
  uploadFileV2,
  updateAddQuestionsInBetween,
  updateQuestionToCacheV2,
};

QuestionLoader.displayName = "QuestionLoader";

QuestionLoader.propTypes = {
  messages: PropTypes.array,
  sections: PropTypes.array,
  worksheetMutationData: PropTypes.object,
  toggleAiQuestionGenerator: PropTypes.func,
  currentAIState: PropTypes.string,
  isGeneratingAIQuestions: PropTypes.bool,
  activeQuestionId: PropTypes.string,
  getAssessmentQuestions: PropTypes.object,
  currentUsageMetricsId: PropTypes.string,
  cancelRequest: PropTypes.func,
};

QuestionLoader.defaultProps = {
  messages: [],
  sections: [],
  worksheetMutationData: {},
  toggleAiQuestionGenerator: _.noop,
  currentAIState: "",
  isGeneratingAIQuestions: false,
  activeQuestionId: "",
  getAssessmentQuestions: {},
  updateWorksheetAIDebugInfo: _.noop,
  currentUsageMetricsId: "",
  cancelRequest: _.noop,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIConversations, ownProps: { isData, isLoading } }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );
      const networkStatus = _.get(getAIConversations, "networkStatus");
      const messages = [..._.get(queryData, "messages.edges", [])].reverse();
      const pageInfo = _.get(queryData, "messages.pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const conversationMetadata = _.get(queryData, "metadata", {});

      return {
        messages,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        conversations: queryData,
        isInitialState: _.isEmpty(messages),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        conversationMetadata,
        getAiConversationRefetch: getAIConversations.refetch,
      };
    },
  })
)(QuestionLoader);
